/**
 * watchdogLog — Registro local de eventos de travamento do Player.
 *
 * FLUXO COMPLETO:
 *  1. Watchdog detecta anomalia (vídeo congelado / imagem presa)
 *  2. watchdogLog.push(event) → grava no localStorage (persiste offline)
 *  3. Heartbeat roda (a cada 10s) → telasService.atualizarHeartbeat()
 *  4. heartbeat chama watchdogLog.getPendingAndLock() → lê eventos
 *  5. Supabase.update({ ultimo_watchdog_log: events }) → envia
 *  6. Se bem-sucedido → watchdogLog.clear() → limpa o buffer local
 *  7. Se falhar (offline) → eventos permanecem para a próxima tentativa
 */

import type { WatchdogEvent } from '../types';

const LOG_KEY     = 'ds:watchdog_log';
const MAX_ENTRIES = 50; // evita crescimento ilimitado do localStorage

export const watchdogLog = {
  /**
   * Adiciona um evento ao log local.
   * Chamado pelo useWatchdog no momento da detecção.
   */
  push(event: Omit<WatchdogEvent, 'ts'>): void {
    try {
      const existing = this.getAll();
      const updated: WatchdogEvent[] = [
        { ...event, ts: new Date().toISOString() },
        ...existing,
      ].slice(0, MAX_ENTRIES); // mantém os 50 mais recentes
      localStorage.setItem(LOG_KEY, JSON.stringify(updated));
    } catch {
      // localStorage pode estar cheio ou indisponível — ignora silenciosamente
    }
  },

  /** Retorna todos os eventos pendentes sem removê-los */
  getAll(): WatchdogEvent[] {
    try {
      const raw = localStorage.getItem(LOG_KEY);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      // JSON válido que não é array ({}, null, texto) travava o buffer: o spread do push lançava e
      // o evento novo não entrava. Tratado como vazio, o próximo push sobrescreve o valor ruim.
      if (!Array.isArray(parsed)) return [];
      // Itens que não são objeto ([null], texto) quebravam o heartbeat ao ler ev.telaId.
      return parsed.filter((e): e is WatchdogEvent => e !== null && typeof e === 'object');
    } catch {
      return [];
    }
  },

  /** Remove todos os eventos do buffer local (chamado após envio bem-sucedido) */
  clear(): void {
    try {
      localStorage.removeItem(LOG_KEY);
    } catch {
      // ignore
    }
  },

  /** Atalho: há eventos aguardando envio? */
  hasPending(): boolean {
    return this.getAll().length > 0;
  },
};
